import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X, Check, ChevronDown } from "lucide-react";

import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";

/**
 * Multi-select checkbox dropdown filter — one option per line, click to
 * toggle. Trigger shows a count badge when selections exist.
 */
function MultiSelect({
  triggerLabel,
  placeholder,
  options,
  selected,
  onToggle,
  triggerTestId,
  contentTestId,
  optionTestIdPrefix,
}) {
  const [open, setOpen] = useState(false);
  const count = selected.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={triggerTestId}
          className="h-10 min-w-[180px] justify-between border-border-subtle bg-surface text-text-primary hover:bg-offwhite"
        >
          <span className="truncate">
            {count === 0 ? placeholder : `${triggerLabel} (${count})`}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 text-text-secondary" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-80 w-64 overflow-y-auto border-border-subtle bg-surface p-1"
        data-testid={contentTestId}
      >
        {options.length === 0 && (
          <div className="px-2 py-3 text-xs text-text-secondary">—</div>
        )}
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              role="menuitemcheckbox"
              aria-checked={active}
              onClick={() => onToggle(opt)}
              data-testid={`${optionTestIdPrefix}-${opt}`}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                active ? "bg-offwhite" : "hover:bg-offwhite"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded border border-border-subtle bg-surface">
                {active && <Check className="h-3 w-3 text-charcoal" />}
              </span>
              <span className="truncate">{opt}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export function ProductFilters({
  search,
  onSearchChange,
  categories: allCategories,
  brands: allBrands,
  selectedCategories,
  selectedBrands,
  onToggleCategory,
  onToggleBrand,
  onClear,
  isFiltered,
}) {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface p-4 lg:flex-row lg:items-center"
      data-testid="product-filters"
    >
      <div className="relative w-full lg:flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        <Input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("products.search_placeholder")}
          data-testid="product-search"
          className="h-10 bg-surface pl-9 border-border-subtle focus-visible:ring-champagne"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:flex lg:w-auto">
        <MultiSelect
          triggerLabel={t("products.filter_categories")}
          placeholder={t("products.all_categories")}
          options={allCategories}
          selected={selectedCategories}
          onToggle={onToggleCategory}
          triggerTestId="filter-category-trigger"
          contentTestId="filter-category-content"
          optionTestIdPrefix="filter-category"
        />
        <MultiSelect
          triggerLabel={t("products.filter_brands")}
          placeholder={t("products.all_brands")}
          options={allBrands}
          selected={selectedBrands}
          onToggle={onToggleBrand}
          triggerTestId="filter-brand-trigger"
          contentTestId="filter-brand-content"
          optionTestIdPrefix="filter-brand"
        />
      </div>

      {isFiltered && (
        <Button
          variant="ghost"
          onClick={onClear}
          data-testid="filter-clear"
          className="text-taupe hover:bg-offwhite hover:text-charcoal"
        >
          <X className="mr-1 h-4 w-4" />
          {t("products.clear_filters")}
        </Button>
      )}
    </div>
  );
}
